import { ButtonProps } from '@headlessui/react';
import React from 'react';
import { Button } from '../catalyst-ui-kit/button';
import { Loader } from '../Loader';

export type SubmitTxStatus = 'waiting-signature' | 'submitting' | 'complete' | undefined;

export interface SubmitTxButtonProps extends Omit<ButtonProps, 'onSubmit'> {
  /**
   * Transaction or signature status.
   * - `undefined`: Waiting for button press
   * - `'waiting-signature'`: Pressed, waiting for user to sign in wallet
   * - `'submitting'`: Transaction or signature submitted, waiting for confirmation/processing
   * - `'complete'`: Transaction or signature confirmed/processed
   */
  status?: SubmitTxStatus;

  /**
   * Initial button text before user press
   */
  actionText: string;

  /**
   * Button text when mutation is complete
   */
  completeText?: string;

  /**
   * Button text
   */
  submittingText?: string;

  disabled?: boolean;
  onSubmit?: (e: React.MouseEvent) => void;
  onComplete?: (e: React.MouseEvent) => void;
}

/**
 * Button for handling transaction or signature submission states.
 */
export const SubmitTxButton: React.FC<SubmitTxButtonProps> = ({
  status,
  actionText,
  completeText = 'Continue',
  submittingText = 'Submitting...',
  disabled,
  onSubmit,
  onComplete,
  ...props
}) => {
  const isProcessing = status === 'waiting-signature' || status === 'submitting';

  const getButtonText = () => {
    if (status === 'complete') return completeText;
    if (status === 'waiting-signature') return 'Waiting for wallet...';
    if (status === 'submitting')
      return (
        <div className="flex items-center space-x-2">
          <Loader className="inline size-2" />
          <span>{submittingText}</span>
        </div>
      );
    return actionText;
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.preventDefault();
    onComplete?.(e);
  };

  return (
    <Button
      {...props}
      color="blue"
      disabled={disabled || isProcessing || (status === 'complete' && !onComplete)}
      onClick={status === 'complete' ? handleComplete : onSubmit}
      className="w-full"
    >
      {getButtonText()}
    </Button>
  );
};
